import { CheckCircle2 } from "lucide-react";
import { EmptyState } from "@/components/ui/feedback";
import { ErrorState, LoadingState, NotAvailableState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { formatUsd } from "@/lib/format-money";

/**
 * The reconciliation exception queue's state surface (J7, /reconciliation/exceptions).
 *
 * Display only. This component owns no fetching: the page/caller passes the data
 * or the failure, so every branch is exercised by a deterministic test and no
 * branch can quietly invent a value. "empty" (a real API returned nothing),
 * "loading", "error" (a real call failed) and "not available" (no reconciliation
 * service exists in this environment) are kept distinguishable — see
 * page-delivery-pipeline.md G2 `defer` and the `NotAvailableState` doc comment
 * in components/ui/system-state.tsx.
 */

export type ReconciliationException = {
  id: string;
  /** The account the break was found on. */
  walletAlias: string;
  /** Short classification of the break. */
  breakType: string;
  /** Which side of the comparison the break was found on. */
  breakSource: string;
  /**
   * Raw USD amount exactly as the API returned it. `null` (or a blank/NaN
   * string) renders an honest dash — never `$0.00`, which would claim a break
   * worth nothing.
   */
  amountUsd: string | null;
  /** When the break was detected. Absent renders an honest "not reported". */
  detectedAt?: string;
};

export type ExceptionsError = {
  message: string;
  /** Only when the gateway actually returned one — never synthesised. */
  supportReference?: string;
};

const NO_DETECTED_AT = "Not reported";

export function ReconciliationExceptionsStateView({
  exceptions,
  loading,
  error,
  unavailableReason,
}: {
  /** `null` while unavailable/loading; `[]` means a real call returned no breaks. */
  exceptions: ReconciliationException[] | null;
  loading: boolean;
  error: ExceptionsError | null;
  /** Set when no reconciliation service exists in this environment (G2 defer). */
  unavailableReason?: string;
}) {
  if (unavailableReason) {
    return (
      <NotAvailableState
        title="Exception queue is not available yet"
        description="There is no reconciliation service in this environment: no break storage, no matching run, and no resolution record."
        reason={unavailableReason}
      />
    );
  }

  if (loading) {
    return (
      <div>
        <p className="mb-3 text-sm text-muted">Loading exceptions...</p>
        <LoadingState rows={3} />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Exceptions could not be loaded"
        description={error.message}
        // The reference is the one thing support can act on. When the gateway did
        // not return one we say so, rather than showing an empty label or a
        // made-up id.
        details={
          error.supportReference
            ? `Support reference: ${error.supportReference}`
            : "No support reference was returned for this failure."
        }
      />
    );
  }

  if (!exceptions || exceptions.length === 0) {
    return (
      <EmptyState
        tone="positive"
        icon={<CheckCircle2 aria-hidden className="size-5 text-success" />}
        title="No open exceptions"
        description="Nothing is open in this environment."
      />
    );
  }

  return (
    <TableWrapper>
      <Table className="dense">
        <thead>
          <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
            <th className="px-4 py-2.5">Wallet</th>
            <th className="px-4 py-2.5">Break</th>
            <th className="px-4 py-2.5">Found on</th>
            <th className="px-4 py-2.5 text-right">Amount (USD)</th>
            <th className="px-4 py-2.5">Detected</th>
          </tr>
        </thead>
        <tbody>
          {exceptions.map((exception) => (
            <tr key={exception.id} className="border-b border-line last:border-b-0">
              <td className="px-4 py-2.5 text-[13px] font-semibold text-ink">{exception.walletAlias}</td>
              <td className="px-4 py-2.5 text-[13px] text-text">{exception.breakType}</td>
              <td className="px-4 py-2.5 text-[13px] text-muted">{exception.breakSource}</td>
              {/* One shared formatter (lib/format-money.ts) decides decimals,
                  separators, sign position and the unknown-value dash, so no
                  page can print a raw API string as a currency figure. */}
              <td className="px-4 py-2.5 text-right font-mono text-[13px] text-ink">
                {formatUsd(exception.amountUsd)}
              </td>
              <td className="px-4 py-2.5 text-[13px] text-muted">{exception.detectedAt ?? NO_DETECTED_AT}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}
